// Maps a MusicXML IR into FlowFrame's tempo state — base tempo, the
// tempoChanges array, and the flags that gate them.
//
// Why type:"sudden": MusicXML <sound tempo="N"> markings are step changes
// (instantaneous), not ramps. timingWorker.js applies a "sudden" change by
// snapping effectiveTempo to endTempo once startMeasure is reached, then
// holding it until a later change wins. That matches MusicXML semantics.
//
// Why the first marking sets the base tempo: scores often place the first
// explicit "♩=N" above measure 6 or so (after a system break). If we kept
// FlowFrame's default 120 BPM as the base, measures 1..first-1 would play at
// the wrong tempo. Bleeding the first marking back to m1 is a much more
// useful default than 120.

import { DEFAULT_TEMPO_CHANGE } from "../../data/musicData";

const FALLBACK_TEMPO = 120;

/**
 * @param {{ tempoMarkings?: Array<{ measureIndex: number, tempo: number }> }} ir
 * @returns {{
 *   tempo: number,
 *   useTempoChanges: boolean,
 *   enableAdditionalTempoChanges: boolean,
 *   tempoChanges: Array<object>,
 * }}
 */
export function buildTempoState(ir) {
  const markings = (ir && ir.tempoMarkings) || [];

  if (markings.length === 0) {
    return {
      tempo: FALLBACK_TEMPO,
      useTempoChanges: false,
      enableAdditionalTempoChanges: false,
      tempoChanges: [{ ...DEFAULT_TEMPO_CHANGE }],
    };
  }

  const baseTempo = Math.round(markings[0].tempo);
  const subsequent = markings.slice(1);

  if (subsequent.length === 0) {
    return {
      tempo: baseTempo,
      useTempoChanges: false,
      enableAdditionalTempoChanges: false,
      tempoChanges: [{ ...DEFAULT_TEMPO_CHANGE }],
    };
  }

  let prevTempo = baseTempo;
  const tempoChanges = subsequent.map((m) => {
    const tempo = Math.round(m.tempo);
    const tc = {
      type: "sudden",
      startMeasure: m.measureIndex + 1,
      endMeasure: m.measureIndex + 1,
      startTempo: prevTempo,
      endTempo: tempo,
      noteValue: null,
    };
    prevTempo = tempo;
    return tc;
  });

  return {
    tempo: baseTempo,
    useTempoChanges: true,
    enableAdditionalTempoChanges: true,
    tempoChanges,
  };
}
